import type { ComponentProps } from "react";
import Markdown from "react-markdown";

import type { AnswerGroupOption } from "@/shared/models/legacy-types";
import { RadioGroup } from "@/shared/ui";
import { tw } from "@/shared/utils";

type RadioGroupProps = ComponentProps<typeof RadioGroup.Root>;

type AnswerRadioGroupProps<TValue> = Omit<
  RadioGroupProps,
  "id" | "value" | "onValueChange"
> & {
  options: readonly AnswerGroupOption<TValue>[];
  id: string;
  warning?: JSX.Element;
  value?: TValue;
  onValueChange: (value: TValue) => void;
};

export const AnswerRadioGroup = <TValue extends string | number>({
  id,
  options,
  warning,
  className,
  value,
  onValueChange,
  ...rest
}: AnswerRadioGroupProps<TValue>) => {
  return (
    <RadioGroup.Root
      className={tw(
        "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
        className,
      )}
      value={value as string}
      onValueChange={(v) => onValueChange(v as TValue)}
      isCard
      {...rest}
    >
      {options.map(({ value, label, showWarning }) => (
        <RadioGroup.Item
          id={`${id}-${value}`}
          key={value}
          // Although RadioGroupPrimitive.Root expects a `value` of type `string`,
          // it is compatible with `number` types as expected for `radio inputs`.
          // Since the `radio input` can be either `string` or `number`, casting to `string`
          // is necessary for compatibility with the radix library component's type.
          value={value as string}
        >
          <RadioGroup.Label className="flex justify-between">
            <Markdown>{label}</Markdown>
            {showWarning && warning}
          </RadioGroup.Label>
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
};
